import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, model } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { ModalService } from '../../../../@core/services/modal/modal.service';
import { BalanceStatus, OpenItemPaymentCombined, RevenueImpact } from '../../../../generated/generated-types';
import { calculateDaysOverdue } from '../../utils/open-item-date.util';


export interface OpenItemArgs {
  openBalancing: (openItem: OpenItemPaymentCombined) => Promise<void>,
  openBalanceInfoToPaymentLinks: (payment: OpenItemPaymentCombined) => void
}

interface OpenItemPaymentCombinedEx extends OpenItemPaymentCombined {
  tooltip: string;
  inPaymentRun?: boolean;
}

interface OpenItemPaymentCombinedExtended extends OpenItemPaymentCombined {
  moneyDirection: string;
}
@Component({
  selector: 'app-open-item-status-column',
  templateUrl: './open-item-status-column.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    BadgeModule,
  ]
})
export class OpenItemStatusColumnComponent {

  modelSig = model<OpenItemPaymentCombinedEx>({} as OpenItemPaymentCombinedEx);

  statusText: string = "";
  @Input() args: OpenItemArgs = {} as OpenItemArgs;


  constructor(
    private i18n: I18nService,
    private modalService: ModalService
  ) {
  }

  ngOnInit() {
    this.setTooltip()
  }

  getOpenItemStatusStyle() {
    this.statusText = "enums.balance-status-";
    let statusStyle = '';

    const dueDate = this.modelSig().dueDate ? new Date(this.modelSig().dueDate) : new Date(this.modelSig().date);
    const daysOverdue = calculateDaysOverdue(dueDate);

    if (this.modelSig().inPaymentRun) {
      this.statusText += 'in-payment-run';
      statusStyle = 'bg-orange-500';
      return statusStyle;
    }

    if (this.modelSig().balanceStatus == BalanceStatus.COMPLETED) {
      this.statusText += BalanceStatus.COMPLETED;
      statusStyle = 'open-item-status-completed';
    }
    else if (daysOverdue > 0 && this.modelSig().accountingType === 'OPENITEM' && this.modelSig().balanceStatus != BalanceStatus.COMPLETED) {
      this.statusText = 'open-items.overdue';
      statusStyle = 'open-item-overdue';
    }
    else if (this.modelSig().balanceStatus == BalanceStatus.PARTIAL) {
      this.statusText += BalanceStatus.PARTIAL;
      statusStyle = 'open-item-status-partial';
    }
    else if (this.modelSig().balanceStatus == BalanceStatus.OPEN) {
      this.statusText += BalanceStatus.OPEN;
      statusStyle = 'open-item-status-open';
    }
    if (this.modelSig().accountingDescription === 'PAYMENT')
      this.statusText += '-payment';
    else if (this.modelSig().accountingDescription === 'OPEN_ITEM')
      this.statusText += '-open-item';

    this.statusText = this.i18n.translate(this.statusText);

    if (daysOverdue > 0 && statusStyle === 'open-item-overdue') {
      const overdueDaysText = this.i18n.translate(_('open-items.overdue-days'), { days: daysOverdue });
      this.statusText = `${this.statusText}\n${overdueDaysText}`;
    }

    return statusStyle;
  }

  openBalanceForCompletedOpenItems() {
    if (this.modelSig().balanceStatus !== BalanceStatus.COMPLETED) {
      return;
    }
    if (this.modelSig().accountingType === 'OPENITEM' || this.modelSig().accountingType === 'PAYMENT') {
      this.args.openBalancing(this.modelSig());
    }
  }

  setTooltip() {
    if (this.modelSig().accountingType === 'OPENITEM') {
      let tooltip = this.i18n.translate(_('open-items.open-item-tooltip'));
      tooltip += `\n${this.i18n.translate('documents.' + this.modelSig().accountingDescription)}`;
      this.modelSig().tooltip = tooltip;
    }
    else if (this.modelSig().accountingDescription === 'CASH_DISCOUNTABLE')
      this.modelSig().tooltip = this.i18n.translate(_('open-items.cash-discountable-tooltip'));
    else if (this.modelSig().accountingDescription === 'COMPLETED_PAYMENT')
      this.modelSig().tooltip = this.i18n.translate(_('open-items.completed-payment-tooltip'));
    else if (this.modelSig().accountingDescription === 'UNDER_PAYED')
      this.modelSig().tooltip = this.i18n.translate(_('open-items.under-payed-tooltip'));
    else if (this.modelSig().accountingDescription === 'OVER_PAYED')
      this.modelSig().tooltip = this.i18n.translate(_('open-items.over-payed-tooltip'));
    else if (this.modelSig().accountingDescription === 'PAYMENT')
      this.modelSig().tooltip = this.i18n.translate(_('open-items.payment-tooltip'));
  }

  getOpenItemStyle() {
    if (this.modelSig().accountingType === 'OPENITEM') {
      if (this.modelSig().documentDirection === 'IN' && this.modelSig().revenueImpact === RevenueImpact.POSITIVE)
        return 'text-primary';
      else if (this.modelSig().documentDirection === 'OUT' && this.modelSig().revenueImpact === RevenueImpact.NEGATIVE)
        return 'text-fuchsia-500';
      else if (this.modelSig().documentDirection === 'IN' && this.modelSig().revenueImpact === RevenueImpact.NEGATIVE)
        return 'text-fuchsia-500';
      else if (this.modelSig().documentDirection === 'OUT' && this.modelSig().revenueImpact === RevenueImpact.POSITIVE)
        return 'text-primary';
    }
    return '';
  }

  getCurrencyValue() {
    return this.modelSig().currency;
  }

  getCurrencyIconClass() {
    return '';
  }

  getCurrencySeverity() {
    if (this.modelSig().revenueImpact === RevenueImpact.NEGATIVE)
      return 'danger';
    else if (this.modelSig().revenueImpact === RevenueImpact.POSITIVE)
      return 'success';
    return 'primary';
  }
}
